import { Component, DestroyRef, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SignalsWelcomePopComponent } from './signals-welcome-pop/signals-welcome-pop.component';
import { BrandSelectionModalComponent } from './brand-selection-modal/brand-selection-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { SignalCardComponent } from './signal-card/signal-card.component';
import { StorageService } from '../../services/storage.service';
import { LocalStorageKeys } from '../../shared/types/storage';
import { catchError, finalize, of, Subject, switchMap } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { BrandsService } from "../../services/brands.service";
import { ListOfSignalsResponse } from "./types";
import { StateService } from "../../services/state.service";
import { DashboardService } from "../../services/dashboard.service";
import {LoaderComponent} from "../../shared/components/loader/loader.component";
import {AssetVersionPipe} from '../../pipes/asset-version.pipe';
import { AuthorizationService } from '../../services/authorization.service';

@Component({
  selector: 'app-signals-screen',
  standalone: true,
  imports: [CommonModule, SignalsWelcomePopComponent, SignalCardComponent, AssetVersionPipe],
  templateUrl: './signals-screen.component.html',
  styleUrl: './signals-screen.component.scss'
})
export class SignalsScreenComponent {
  private dialog = inject(MatDialog);
  private storageService = inject(StorageService);
  private brandsService = inject(BrandsService);
  private stateService = inject(StateService);
  private dashboardService = inject(DashboardService);
  private destroyRef = inject(DestroyRef);
  public currentDate = new Date();
  public listOfSignalsData$ = new Subject<{ brandKeys: string[], accountKey: string }>();
  public listOfSignalsData: ListOfSignalsResponse | undefined;
  public selectedBrands: Record<string, boolean> | null = null;
  public loadingState = true;
  private authorizationService = inject(AuthorizationService);
  protected readonly isTrialUser = this.authorizationService.isTrialUser;

  constructor() {
    const storedVisibility = this.storageService.local.getItem(LocalStorageKeys.MANAGED_BRANDS_SIGNALS_VISIBILITY);

    if (storedVisibility && !Array.isArray(storedVisibility)) {
      this.selectedBrands = storedVisibility;
    }
    this.initListOfSignalsData();

    effect(() => {
      const brands = this.dashboardService.myBrandOptions();
      if (brands && brands.length > 0) {
        this.listOfSignalsData$.next({ brandKeys: this.getSelectedBrandKey(), accountKey: this.stateService.activeAccount() });
      }
    });
  }

  private getSelectedBrandKey(): string[] {
    const options = this.dashboardService.myBrandOptions();
    if (!options) return [];
    return options
      .filter(brand => !this.selectedBrands || this.selectedBrands[brand.key])
      .map(brand => brand.key);
  }

  public openBrandSelectionModal(): void {
    const dialogRef = this.dialog.open(BrandSelectionModalComponent, {
      panelClass: 'brand-selection-modal-panel',
      backdropClass: 'brand-selection-modal-backdrop'
    });

    dialogRef.afterClosed().subscribe((result: Record<string, boolean> | undefined) => {
      if (result) {
        this.selectedBrands = result;
        this.listOfSignalsData$.next({ brandKeys: this.getSelectedBrandKey(), accountKey: this.stateService.activeAccount() });
      }
    });
  }

  private initListOfSignalsData(): void {
    this.listOfSignalsData$.pipe(
      switchMap(payload => {
        this.loadingState = true;
        return this.brandsService.getListOfSignalsByBrands(payload).pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => {
            this.loadingState = false;
          }),
          catchError((error: any) => {
            return of(undefined);
          }),
        );
      })
    ).subscribe(result => {
      this.listOfSignalsData = result;
    });
  }
}
